import { EmployeeContact } from '../../../models/contacts/EmployeeContact';

/**
 * Pure rules over the company employee directory (`GET /V1/employee-emails`).
 *
 * The directory is fetched once per app session and cached in `ContactsService`;
 * everything here operates on that cached list so the same name/search behavior
 * is shared by the desktop form and the mobile flow, and is testable without HTTP.
 */

/**
 * The name-carrying shape these rules read. Both a directory row
 * ({@link EmployeeContact}) and a PO contact's BambooHR insight satisfy it, so
 * one display rule covers both sources and they cannot format names differently.
 */
export interface NamedPerson {
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
}

/**
 * A person's display name: preferred name (falling back to legal first name)
 * plus last name.
 *
 * `preferredName` is null for most employees, which is why the fallback matters —
 * reading it straight would leave most people showing only a surname.
 *
 * @param person - A directory row or BambooHR insight; null/undefined is fine.
 * @returns "Bob Smith", or null when no name is known — callers distinguish
 *   "unknown person" from "person whose name is blank" and fall back themselves.
 */
export function employeeDisplayName(person?: NamedPerson | null): string | null {
  if (!person) { return null; }
  const first = (person.preferredName || person.firstName || '').trim();
  const last = (person.lastName || '').trim();

  return [first, last].filter(Boolean).join(' ') || null;
}

/**
 * Indexes the directory by lower-cased email for O(1) lookup.
 *
 * Built once when the fetch lands rather than scanned per contact — the name is
 * resolved for every contact row, on every render pass, against a directory of
 * hundreds of people.
 *
 * @param employees - The fetched directory.
 */
export function buildEmployeeIndex(employees: EmployeeContact[]): Map<string, EmployeeContact> {
  const index = new Map<string, EmployeeContact>();
  for (const employee of employees || []) {
    const key = (employee?.email || '').trim().toLowerCase();
    if (key) { index.set(key, employee); }
  }
  return index;
}

/**
 * Every address in the directory, in the order returned.
 *
 * Kept as its own derived list because most of the app only needs addresses (the
 * notify list, the recipient checkboxes, the submitted log) and used to receive
 * exactly this from the endpoint.
 *
 * @param employees - The fetched directory.
 */
export function extractEmployeeEmails(employees: EmployeeContact[]): string[] {
  return (employees || [])
    .map(e => (e?.email || '').trim())
    .filter(Boolean);
}

/**
 * Directory addresses for a type-ahead field — addresses only, no names, sorted.
 *
 * Backs the log-edit form's Email field, which stores an address. The field stays
 * a text input with an autocomplete list beside it rather than a true select:
 * a select renders every option as an element up front, and the whole directory
 * rendered twice over is enough to lock up the details modal.
 *
 * @param employees - The fetched directory.
 */
export function employeeEmailChoices(employees: EmployeeContact[]): string[] {
  return [...new Set(extractEmployeeEmails(employees))]
    .sort((a, b) => a.localeCompare(b));
}

/**
 * Directory names for a type-ahead field — names only, no addresses, sorted.
 *
 * Backs the log-edit form's Logged By field, which stores a person's name rather
 * than an address. Deduped, because the field holds a name: two people who share
 * one are indistinguishable in the stored data either way.
 *
 * @param employees - The fetched directory.
 */
export function employeeNameChoices(employees: EmployeeContact[]): string[] {
  const names = (employees || [])
    .map(e => employeeDisplayName(e))
    .filter((n): n is string => !!n);

  return [...new Set(names)].sort((a, b) => a.localeCompare(b));
}

/**
 * The part of an address before the `@`, lower-cased.
 *
 * Used by {@link searchEmployees}: the directory is a single company domain, so
 * the domain carries no information to search on and actively breaks matching for
 * any query token it happens to contain.
 *
 * @param email - A raw address; anything without an `@` is returned as-is.
 */
function emailLocalPart(email: string): string {
  const at = email.indexOf('@');
  return (at === -1 ? email : email.slice(0, at)).toLowerCase();
}

/**
 * Directory search for the "add employee" box.
 *
 * Matches on every token in the query rather than the raw string, so a PO name
 * ("Jane Doe") finds the person whose address spells it differently
 * (jane.doe@macmiller.com). Each token has to appear in the name OR the address,
 * so "jane service" can't match and "doe jane" can. A single-word query behaves
 * as a substring.
 *
 * Only the part of the address before the `@` is searched. Every employee shares
 * the same company domain, so searching the whole address makes any token found
 * in that domain ("mac", "miller", "com") match the entire directory and filter
 * nothing — which is exactly what surnames like Miller need to work.
 *
 * @param employees - The fetched directory.
 * @param query - Raw text from the search box.
 * @param excludeEmails - Addresses already on the notify list; filtered out so the
 *   user can't add the same person twice.
 * @param limit - Max rows to return (the list is rendered inline under the input).
 */
export function searchEmployees(
  employees: EmployeeContact[],
  query: string,
  excludeEmails: string[] = [],
  limit: number = 10
): EmployeeContact[] {
  const tokens = (query || '').toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);
  if (!tokens.length) { return []; }

  const excluded = new Set(excludeEmails.map(e => (e || '').trim().toLowerCase()));

  return (employees || [])
    .filter(employee => {
      const email = (employee?.email || '').trim().toLowerCase();
      if (!email || excluded.has(email)) { return false; }
      // Name and address are searched together: people look someone up by either.
      // The domain is dropped first — see the note above.
      const haystack = `${employeeDisplayName(employee) || ''} ${emailLocalPart(email)}`.toLowerCase();
      return tokens.every(t => haystack.includes(t));
    })
    .slice(0, limit);
}

/** The domain every typed address is completed with. */
export const COMPANY_EMAIL_DOMAIN = '@macmiller.com';

/** An "add this email" suggestion built from what the user typed. */
export interface TypedEmail {
  /** The full, lower-cased address. */
  email: string;
  /**
   * How it was built, so the UI can say so:
   *  - 'as-typed'     — the user typed the whole address
   *  - 'domain-added' — the user typed the part before the @; the domain was appended
   *  - 'from-name'    — the user typed (or Find seeded) "First Last"; built as first.last
   */
  source: 'as-typed' | 'domain-added' | 'from-name';
}

/**
 * The address to offer as an "add this email" row for what's in the "add
 * employee" box, or null when there is nothing to offer.
 *
 * The directory comes from BambooHR, so a BambooHR outage leaves it empty and the
 * search can match no one. Building an address from the typed text keeps the
 * notify list usable then, and covers anyone the report leaves out:
 *
 *  - "jane.doe@macmiller.com" → as typed. Any other domain → null (internal picker).
 *  - "jane.doe"               → domain appended.
 *  - "Jane Doe" / "Jane Q Doe 555-1234" → first and last name words joined with a
 *    period, domain appended. This is a guess, which is why `source` says so: the
 *    Find button on a name-only PO contact seeds the box with exactly this.
 *
 * Returns null when the directory already lists the address (its search result
 * row offers it) or it is already on the notify list.
 *
 * @param query - Raw text from the search box.
 * @param directoryEmails - Every address in the fetched directory (may be empty).
 * @param excludeEmails - Addresses already on the notify list.
 */
export function typedEmailToAdd(
  query: string,
  directoryEmails: string[],
  excludeEmails: string[] = []
): TypedEmail | null {
  const built = buildTypedEmail((query || '').trim().toLowerCase());
  if (!built) { return null; }

  const taken = (list: string[]) => (list || []).some(e => (e || '').trim().toLowerCase() === built.email);
  if (taken(directoryEmails) || taken(excludeEmails)) { return null; }

  return built;
}

function buildTypedEmail(text: string): TypedEmail | null {
  if (!text) { return null; }

  if (text.includes('@')) {
    return /^[a-z0-9._%+-]+@macmiller\.com$/.test(text) ? { email: text, source: 'as-typed' } : null;
  }

  if (/^[a-z0-9._%+-]+$/.test(text)) {
    return { email: text + COMPANY_EMAIL_DOMAIN, source: 'domain-added' };
  }

  // A name: keep the word tokens (drops phone numbers and stray punctuation), use
  // first and last so a middle name or initial doesn't end up in the address.
  const words = text.split(/\s+/)
    .map(w => w.replace(/[^a-z-]/g, ''))
    .filter(w => /^[a-z]/.test(w));
  if (words.length < 2) { return null; }

  return { email: `${words[0]}.${words[words.length - 1]}${COMPANY_EMAIL_DOMAIN}`, source: 'from-name' };
}
